import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Organization } from './models';
import { baseApi } from '@centerpiece/components';

export const organization = baseApi
  .enhanceEndpoints({ addTagTypes: ['Organizations', 'Organization'] })
  .injectEndpoints({
    endpoints: builder => ({
      getOrganizations: builder.query<Organization[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'organization',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'Organization' as const,
                  id,
                })),
                'Organizations',
              ]
            : ['Organizations'],
      }),
      getOrganization: builder.query<
        Organization,
        { id: number; params?: object }
      >({
        query: ({ id, params }: { id: number; params?: object }) => ({
          url: `organization/${id}`,
          method: 'GET',
          params: params ? { nested: 'all', ...params } : { nested: 'all' },
        }),
        providesTags: (result, error, arg) => [
          { type: 'Organization', id: arg.id },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createOrganization: builder.mutation({
        query: request => ({
          url: 'organization',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Organizations'],
      }),
      updateOrganization: builder.mutation<Organization, Organization>({
        query: request => ({
          url: `organization/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Organization', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            organization.util.updateQueryData(
              'getOrganization',
              { id },
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(organization.util.invalidateTags(['Organizations']))
             */
            dispatch(organization.util.invalidateTags(['Organizations']));
          }
        },
      }),
      deleteOrganization: builder.mutation<Organization, number>({
        query: id => ({
          url: `organization/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'Organization' as const, arg }, 'Organizations']
            : ['Organizations'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetOrganizationsQuery,
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  usePrefetch: useOrganizationsPrefetch,
} = organization;
const selectOrganizationsRoot = organization.endpoints.getOrganizations.select(
  {}
);
export const selectOrganizations = createSelector(
  selectOrganizationsRoot,
  organization => organization.data || []
);
